import React, { useState } from 'react';
import type { MenuProps } from 'antd';
import { Button, Space, Dropdown, Modal, message, Segmented, Select, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faListUl, faChevronDown, faTableColumns, faCalendarDays } from '@fortawesome/pro-solid-svg-icons';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getPlannerPages, deletePlannerPage } from '../../services/tasks';
import BoardView from './components/BoardView';
import ListView from './components/ListView';
import CalendarView from './components/CalendarView';
import CreatePlannerPageModal from './components/CreatePlannerPageModal';
import CreateTaskModal from './components/CreateTaskModal';
import FilterBar from './components/FilterBar';
import ConfigurationPanel from './components/ConfigurationPanel';
import BacklogPanel from './components/BacklogPanel';
import TaskDetailModal from './components/TaskDetailModal';
import { colors } from '../../theme/colors';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const BREAKPOINT_NARROW = 992;

function useNarrowBreakpoint(breakpoint: number): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [breakpoint]);
  return narrow;
}

const Planner: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pageId: legacyPageId } = useParams<{ pageId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'board' | 'list' | 'calendar'>(() => {
    const requestedView = searchParams.get('view');
    if (requestedView === 'board' || requestedView === 'list' || requestedView === 'calendar') {
      return requestedView;
    }
    const savedView = typeof window !== 'undefined' ? window.localStorage.getItem('planner-view') : null;
    return savedView === 'list' || savedView === 'calendar' ? savedView : 'board';
  });
  const [showCreatePageModal, setShowCreatePageModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [createTaskDefaults, setCreateTaskDefaults] = useState<{
    status?: string;
    swimlane?: string;
    dueDate?: string;
  }>({});
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [showBacklogPanel, setShowBacklogPanel] = useState(false);
  const [filters, setFilters] = useState<any>({});

  const { data: plannerPages = [], isLoading, refetch } = useQuery({
    queryKey: ['planner-pages'],
    queryFn: getPlannerPages,
  });

  const requestedPageId = searchParams.get('page') || legacyPageId || null;
  const requestedTaskId = searchParams.get('task');

  const selectedPage =
    plannerPages.find((page) => page.id === requestedPageId || page.slug === requestedPageId) ||
    plannerPages.find((page) => page.id === selectedPageId) ||
    plannerPages[0];

  // Open the planner page encoded in a notification deep link, or fall back to the first page.
  React.useEffect(() => {
    if (plannerPages.length === 0) return;

    const linkedPage = requestedPageId
      ? plannerPages.find((page) => page.id === requestedPageId || page.slug === requestedPageId)?.id || null
      : null;

    if (linkedPage && linkedPage !== selectedPageId) {
      setSelectedPageId(linkedPage);
    } else if (!selectedPageId) {
      setSelectedPageId(plannerPages[0].id);
    }
  }, [plannerPages, requestedPageId, selectedPageId]);

  // Replace legacy UUID page parameters with the canonical readable slug.
  React.useEffect(() => {
    if (!selectedPage?.slug || requestedPageId === selectedPage.slug) return;
    if (requestedPageId && requestedPageId !== selectedPage.id && requestedPageId !== legacyPageId) return;
    if (legacyPageId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('page', selectedPage.slug);
      navigate(`/planner?${nextParams.toString()}`, { replace: true });
      return;
    }
    setSearchParams((current) => {
      const nextParams = new URLSearchParams(current);
      nextParams.set('page', selectedPage.slug);
      return nextParams;
    }, { replace: true });
  }, [legacyPageId, navigate, requestedPageId, searchParams, selectedPage, setSearchParams]);

  const handlePageChange = (pageId: string) => {
    setSelectedPageId(pageId);
    const pageReference = plannerPages.find((page) => page.id === pageId)?.slug || pageId;
    setSearchParams((current) => {
      const nextParams = new URLSearchParams(current);
      nextParams.set('page', pageReference);
      nextParams.delete('task');
      return nextParams;
    }, { replace: true });
  };

  const handleTaskLinkChange = (taskId?: string) => {
    setSearchParams((current) => {
      const nextParams = new URLSearchParams(current);
      if (selectedPage) nextParams.set('page', selectedPage.slug || selectedPage.id);
      if (taskId) nextParams.set('task', taskId);
      else nextParams.delete('task');
      return nextParams;
    }, { replace: true });
  };

  const replaceTaskReference = React.useCallback((taskReference: string) => {
    setSearchParams((current) => {
      const nextParams = new URLSearchParams(current);
      if (selectedPage) nextParams.set('page', selectedPage.slug || selectedPage.id);
      nextParams.set('task', taskReference);
      return nextParams;
    }, { replace: true });
  }, [selectedPage, setSearchParams]);

  const openCreateTask = (defaults: { status?: string; swimlane?: string; dueDate?: string } = {}) => {
    setCreateTaskDefaults(defaults);
    setShowCreateTaskModal(true);
  };

  const handleViewModeChange = (nextView: 'board' | 'list' | 'calendar') => {
    setViewMode(nextView);
    window.localStorage.setItem('planner-view', nextView);
    setSearchParams((current) => {
      const nextParams = new URLSearchParams(current);
      nextParams.set('view', nextView);
      return nextParams;
    }, { replace: true });
  };

  const deletePageMutation = useMutation({
    mutationFn: (id: string) => deletePlannerPage(id),
    onSuccess: () => {
      message.success('Planner page deleted');
      // Refetch pages and clear selection; effect will pick first page
      queryClient.invalidateQueries({ queryKey: ['planner-pages'] });
      setSelectedPageId(null);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete planner page');
    },
  });

  const handleBoardActionClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'newPage') {
      setShowCreatePageModal(true);
      return;
    }
    if (key === 'backlog') {
      setShowBacklogPanel(true);
      return;
    }
    if (!selectedPage) return;

    if (key === 'configure') {
      setShowConfigPanel(true);
      return;
    }

    if (key === 'delete') {
      Modal.confirm({
        title: `Delete planner page "${selectedPage.name}"?`,
        content: 'This will delete the board and all its tasks. This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => deletePageMutation.mutate(selectedPage.id),
      });
    }
  };

  const isNarrow = useNarrowBreakpoint(BREAKPOINT_NARROW);

  const actionsMenu: MenuProps = {
    onClick: handleBoardActionClick,
    items: [
      ...(isNarrow
        ? [
            { key: 'newPage', label: 'New Planner Page' },
            { key: 'backlog', label: 'Backlog' },
            { type: 'divider' as const },
          ]
        : []),
      {
        key: 'configure',
        label: 'Configure board',
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: <span style={{ color: '#ff4d4f' }}>Delete board</span>,
      },
    ],
  };

  const plannerOptions = plannerPages.map((page) => ({
    value: page.id,
    label: page.name,
  }));

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: colors.bgBase, boxSizing: 'border-box', maxWidth: '100%', overflowX: 'auto' }}>
      <div style={{ marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ color: colors.textPrimary, margin: 0, marginBottom: '4px', fontSize: 26 }}>Planner</h1>
          <p style={{ color: colors.textMuted, margin: 0 }}>
            {selectedPage ? selectedPage.description || 'Plan, assign, and move work forward.' : 'Create a planner page to get started'}
          </p>
        </div>
        <Space>
          {!isNarrow && (
            <Button
              type="default"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setShowCreatePageModal(true)}
            >
              New Planner Page
            </Button>
          )}
          {selectedPage && (
            <>
              {!isNarrow && (
                <Button
                  type="default"
                  icon={<FontAwesomeIcon icon={faListUl} />}
                  onClick={() => setShowBacklogPanel(true)}
                >
                  Backlog
                </Button>
              )}
              <Dropdown menu={actionsMenu} trigger={['click']} placement="bottomRight">
                <Button
                  type="default"
                  icon={<FontAwesomeIcon icon={faChevronDown} />}
                >
                  Actions
                </Button>
              </Dropdown>
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => openCreateTask()}
              style={{ background: colors.primary, borderColor: colors.primary }}
            >
              New Task
            </Button>
            </>
          )}
        </Space>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>Loading planner pages...</div>
      ) : plannerPages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: colors.textMuted }}>
          <p style={{ fontSize: '16px', marginBottom: '16px' }}>No planner pages yet</p>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setShowCreatePageModal(true)}
            size="large"
            style={{ background: '#DB50AC', borderColor: '#DB50AC' }}
          >
            Create Your First Planner Page
          </Button>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <Select
              className="planner-page-select"
              aria-label="Select planner page"
              value={selectedPage?.id}
              onChange={handlePageChange}
              options={plannerOptions}
              showSearch
              optionFilterProp="label"
              style={{ width: isNarrow ? '100%' : 280 }}
            />
            <Segmented
              className="planner-view-switch"
              aria-label="Planner view"
              value={viewMode}
              onChange={(value) => handleViewModeChange(value as 'board' | 'list' | 'calendar')}
              options={[
                {
                  label: <Tooltip title="Board"><FontAwesomeIcon icon={faTableColumns} aria-label="Board" /></Tooltip>,
                  value: 'board',
                },
                {
                  label: <Tooltip title="List"><FontAwesomeIcon icon={faListUl} aria-label="List" /></Tooltip>,
                  value: 'list',
                },
                {
                  label: <Tooltip title="Calendar"><FontAwesomeIcon icon={faCalendarDays} aria-label="Calendar" /></Tooltip>,
                  value: 'calendar',
                },
              ]}
            />
          </div>

          {selectedPage && (
            <>
              <FilterBar
                plannerPage={selectedPage}
                filters={filters}
                onFiltersChange={setFilters}
                members={selectedPage.members}
              />
              {viewMode === 'board' && (
                <BoardView
                  plannerPage={selectedPage}
                  filters={filters}
                  onTaskOpen={(taskId) => handleTaskLinkChange(taskId)}
                  onCreateTask={openCreateTask}
                />
              )}
              {viewMode === 'list' && (
                <ListView plannerPage={selectedPage} filters={filters} onTaskOpen={handleTaskLinkChange} />
              )}
              {viewMode === 'calendar' && (
                <CalendarView
                  plannerPage={selectedPage}
                  filters={filters}
                  onTaskOpen={handleTaskLinkChange}
                  onCreateTask={(dueDate) => openCreateTask({ dueDate })}
                />
              )}
            </>
          )}
        </>
      )}

      <CreatePlannerPageModal
        open={showCreatePageModal}
        onClose={() => setShowCreatePageModal(false)}
        onSuccess={() => {
          setShowCreatePageModal(false);
          refetch();
        }}
      />

      {selectedPage && (
        <>
        <CreateTaskModal
          open={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          onSuccess={() => {
            setShowCreateTaskModal(false);
            // Invalidate tasks query to refresh the board
            queryClient.invalidateQueries({ queryKey: ['tasks', selectedPage.id] });
          }}
          plannerPage={selectedPage}
          initialStatus={createTaskDefaults.status}
          initialSwimlane={createTaskDefaults.swimlane}
          initialDueDate={createTaskDefaults.dueDate}
        />
          <ConfigurationPanel
            plannerPage={selectedPage}
            open={showConfigPanel}
            onClose={() => setShowConfigPanel(false)}
          />
          <BacklogPanel
            plannerPage={selectedPage}
            open={showBacklogPanel}
            onClose={() => setShowBacklogPanel(false)}
            onTaskOpen={(taskId) => {
              setShowBacklogPanel(false);
              handleTaskLinkChange(taskId);
            }}
          />
          {requestedTaskId && (
            <TaskDetailModal
              taskId={requestedTaskId}
              open
              onClose={() => handleTaskLinkChange()}
              onUpdate={() => queryClient.invalidateQueries({ queryKey: ['tasks', selectedPage.id] })}
              plannerPage={selectedPage}
              onResolvedReference={replaceTaskReference}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Planner;
